import { useState } from 'react';
import { X, Plus, Trash2, RotateCcw, Check, Pencil } from 'lucide-react';
import { LIMITS, SHORTCUT_KEY_PATTERN } from '@superchat/shared/constants';
import { useShortcuts } from '../lib/shortcuts.jsx';

/**
 * One row: either a summary or an inline editor for it.
 *
 * Editing happens in place rather than in a second dialog — the list is the
 * context that tells you whether the wording you are changing still reads
 * differently from the one below it.
 */
function Row({ shortcut, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [key, setKey] = useState(shortcut.key);
  const [text, setText] = useState(shortcut.text);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave({ key: key.trim().toLowerCase(), text });
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await onDelete();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <li className="shortcut">
        <div className="shortcut__head">
          <span className="shortcut__key mono">/{shortcut.key}</span>
          <div className="shortcut__actions">
            <button
              type="button"
              className="icon-button"
              onClick={() => setEditing(true)}
              aria-label={`Edit /${shortcut.key}`}
            >
              <Pencil size={12} />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={remove}
              disabled={busy}
              aria-label={`Delete /${shortcut.key}`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
        <p className="shortcut__text">{shortcut.text}</p>
        {error && <div className="login__error">{error}</div>}
      </li>
    );
  }

  return (
    <li className="shortcut is-editing">
      <label className="field">
        <span className="field__label">TRIGGER</span>
        <input
          className="field__control mono"
          value={key}
          maxLength={32}
          onChange={(e) => setKey(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">REPLY</span>
        <textarea
          className="field__control field__control--area"
          rows={4}
          maxLength={LIMITS.SHORTCUT_TEXT_MAX}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {error && <div className="login__error">{error}</div>}
      <div className="shortcut__foot">
        <button
          type="button"
          className="button"
          onClick={() => {
            setKey(shortcut.key);
            setText(shortcut.text);
            setError(null);
            setEditing(false);
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="button button--primary"
          onClick={save}
          disabled={busy || !SHORTCUT_KEY_PATTERN.test(key.trim().toLowerCase()) || !text.trim()}
        >
          <Check size={12} /> Save
        </button>
      </div>
    </li>
  );
}

/**
 * Manage the `/shortcuts` the composer expands.
 *
 * They are the agent's own, not the app's, which is why nothing here is gated
 * on a permission: an agent editing their own canned replies is not an action
 * on a tenant.
 */
export function ShortcutsDialog({ onClose }) {
  const { shortcuts, create, update, remove, restoreDefaults } = useShortcuts();
  const [key, setKey] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const add = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await create({ key: key.trim().toLowerCase(), text });
      setKey('');
      setText('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const validKey = SHORTCUT_KEY_PATTERN.test(key.trim().toLowerCase());

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div
        className="dialog dialog--wide"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Composer shortcuts"
      >
        <header className="dialog__head">
          <h2>Shortcuts</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <p className="login__sub">
          Type <code>/trigger</code> in the composer and it expands to the reply below. These are
          yours — they follow you across every app you work in.
        </p>

        <form className="shortcut-new" onSubmit={add}>
          <label className="field">
            <span className="field__label">TRIGGER</span>
            <input
              className="field__control mono"
              value={key}
              maxLength={32}
              placeholder="refund-policy"
              onChange={(e) => setKey(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">REPLY</span>
            <textarea
              className="field__control field__control--area"
              rows={3}
              maxLength={LIMITS.SHORTCUT_TEXT_MAX}
              value={text}
              placeholder="What should be inserted when you type it."
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          {key && !validKey && (
            <div className="docs__footnote">
              A trigger is lowercase letters, numbers and dashes — it has to be typeable after a
              slash.
            </div>
          )}
          {error && <div className="login__error">{error}</div>}
          <button
            type="submit"
            className="button button--primary"
            disabled={busy || !validKey || !text.trim()}
          >
            <Plus size={12} /> Add shortcut
          </button>
        </form>

        <ul className="shortcut-list">
          {shortcuts.length === 0 && (
            <li className="table__empty">
              No shortcuts yet. Add one above, or restore the built-in set.
            </li>
          )}
          {shortcuts.map((shortcut) => (
            <Row
              key={shortcut.id}
              shortcut={shortcut}
              onSave={(patch) => update(shortcut.id, patch)}
              onDelete={() => remove(shortcut.id)}
            />
          ))}
        </ul>

        <footer className="dialog__foot">
          <button type="button" className="button" onClick={restoreDefaults}>
            <RotateCcw size={12} /> Restore built-in set
          </button>
          <button type="button" className="button button--primary" onClick={onClose}>
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}

export default ShortcutsDialog;
